import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { DocumentFileType, DocumentSettingType, DomainFieldConfig } from '../type'
import { ToolBarType } from '../onlyofficeFieldSetting/type';
import { TreeDataNode } from 'antd';

// 文件夹项目类型
export interface FolderItem {
    folderId: number;
    folderName: string;
    parentId: number | null;
    level: number;
    folderTmplId: number;
    projectId: number;
    viewId: number;
    fileCount: number;
    id: number;
    fileSize: number;
}

export interface ISettingDomainSourceData {
    fieldTag: string;
    fieldValue: string;
    fieldValueDisplayName: string;
    projectId: string;
}
export interface DocumentSettingProjectData {
    documentDetail?: any,
    type?: DocumentSettingType,
    projectId?: string,
    viewId?: string,
    isJuan?: boolean
}
interface State {
    documentType: DocumentFileType,
    documnetSettingType: DocumentSettingType,
    documentLoading: boolean,
    documentSettingProjectData: DocumentSettingProjectData,
    showFormSetting: boolean, // 是否显示表单设置
    fieldValues: Record<string, any>, // 域值存储
    // 资料包目录树
    packageFolderTreeData: TreeDataNode[],
    packageFolderTreeDataExpandedKeys: string[],
    // 当前选中的工具栏
    selectedToolBar: ToolBarType,
    // 编辑器配置
    editorConfig: any,
    editorConfigBack: any,
    // 编辑器配置加载状态
    editorConfigLoading: boolean,
    // 文档加载状态
    documentReady: boolean,
    // 当前选中的文件数据
    selectedFileData: any,
    // 项目信息字典数据
    projectInfoDictData: any,
    projectInfoDictCodeMap: any,

    /** 项目信息相关 */
    // 项目信息属性值
    projectInfoAttrValues: any,
    showLeftSetting: boolean,
    projectInfoFormEditable: boolean,
    // 项目信息表单数据

    // 字典
    personBaseInfoData: any,
    enterpriseBaseInfoData: any,

    /** 字段域相关 */
    // 字段域的form数据
    domainTreeData: DomainFieldConfig[],
    domainTreeDataExpandedKeys: string[],
    domainTreeCardExpanded: boolean, // 域字段卡片是否展开
    domainShowType: 'flat' | 'tree', // 域字段展示类型：平级或树形
    allDomainTagMap: Record<string, DomainFieldConfig>,
    // setting
    settingDomainFormData: Record<string, any>,
    settingProjectFormData: Record<string, any>,
    // 域字段类型弹出框
    domainFieldTypeModalVisible: boolean,
    clickedFieldInfo: any,
    // 工程详情
    projectDetail: any,

    // 文件编辑标题
    settingProjectTitle: string,

    // 当前文件夹或者视图。详情刚进去的时候会用到
    currentDetailFolder: any,
    // 记录上一级folderId
    parentFolderId: string | number | null,
    // 文档编辑的当前文件夹或者视图。视图切换的时候会用到，关闭会消失，这个时候它的值应该等于 currentDetailFolder
    currentSettingDetailFolderDoc: any,

    // 单个文件工程字段域相关：
    usedDomainDocDataMap: any,

    // 是否修改
    isOnlyOfficeDocumentModified: boolean,

    // 文档编辑模式
    isEditMode: boolean,

    // 是否需要加载域配置（点击同步按钮后为 true，加载完成后为 false）
    needLoadDomainConfig: boolean,
    // 文件夹列表
    folderList: FolderItem[],
    // 文件夹列表加载状态
    folderListLoading: boolean,

    // 是否存在域值被清空
    isExistEmptyDomainField: boolean,
    // 是否存在项目域值被清空
    isExistEmptyProjectField: boolean,
    // 工程详情编辑模式
    deatailEditabled: boolean,
    signImageRes: any
    signFormData: any
}

interface Actions {
    resetStore: () => void
    resetDocumentStore: () => void
    setProjectInfoFormEditable: (editable: boolean) => void
    setDomainTreeCardExpanded: (expanded: boolean) => void
    getAllSettingFormData: () => Record<string, any>
    setEditMode: (mode: boolean) => void
    getPurDomainTreeData: () => any[]
    getDocumentId: () => string | null
}

const documentState: Omit<State, 'currentDetailFolder' | 'folderList' | 'isEditMode'> = {
    documentType: DocumentFileType.WORD,
    documentReady: false,
    documnetSettingType: DocumentSettingType.DOCUMENT_TEMPLATE,
    documentLoading: false,
    documentSettingProjectData: {},
    domainTreeData: [],
    domainTreeDataExpandedKeys: [],
    domainTreeCardExpanded: false, // 默认展开
    domainShowType: 'flat', // 默认平级展示
    showFormSetting: false,
    fieldValues: {},
    parentFolderId: null,
    packageFolderTreeData: [],
    packageFolderTreeDataExpandedKeys: [],
    selectedToolBar: ToolBarType.PACKAGE_INFO,
    editorConfig: null,
    editorConfigBack: null,
    editorConfigLoading: false,
    selectedFileData: null,
    projectInfoDictData: null,
    projectInfoDictCodeMap: {},
    showLeftSetting: false,
    projectInfoAttrValues: [],
    projectInfoFormEditable: true,
    allDomainTagMap: {},
    settingDomainFormData: {},
    settingProjectFormData: {},
    personBaseInfoData: [],
    enterpriseBaseInfoData: [],
    projectDetail: null,
    settingProjectTitle: '',
    usedDomainDocDataMap: {},
    isOnlyOfficeDocumentModified: false,

    needLoadDomainConfig: true, // 默认需要加载域配置
    domainFieldTypeModalVisible: false,
    clickedFieldInfo: null,
    folderListLoading: false,
    isExistEmptyDomainField: false,
    isExistEmptyProjectField: false,
    deatailEditabled: true,
    signImageRes: null,
    signFormData: null,
    currentSettingDetailFolderDoc: null
}

// define the initial state
const initialState: State = {
    ...documentState,
    isEditMode: false, // 默认为查看模式
    folderList: [],
    currentDetailFolder: null,
}

// 清单
const useStore = create<State & Actions>()(
    devtools((set, get) => ({
        ...initialState,
        resetStore: () => {
            set(initialState)
        },
        resetDocumentStore: () => {
            set(documentState)
        },
        setProjectInfoFormEditable: (editable: boolean) => {
            set({ projectInfoFormEditable: editable })
        },
        setDomainTreeCardExpanded: (expanded: boolean) => {
            set({ domainTreeCardExpanded: expanded })
        },
        getAllSettingFormData: () => {
            const { settingDomainFormData, settingProjectFormData } = get()
            return {
                ...settingDomainFormData,
                ...settingProjectFormData
            }
        },
        setEditMode: (mode: boolean) => {
            set({ isEditMode: mode })
        },
        // 获取域需要的树，不包括工程
        getPurDomainTreeData: () => {
            const domainTreeData = get().domainTreeData;
            const result: any[] = [];
            const root = domainTreeData?.[0]
            if (!root?.children) return []
            root.children.forEach((group) => {
                if (group?.groupName) {
                    result.push(group)
                }
            })
            return result
        },
        getDocumentId: () => {
            const documentSettingProjectData = get().documentSettingProjectData
            return documentSettingProjectData?.documentDetail?.id?.toString() || null
        }
    })),
)

export default useStore
